import "./ReportRadarChart.css";

// Pure SVG radar chart — no canvas, no external lib.
// Canvas elements are unreliable in Puppeteer PDF generation.

const MODULES = [
  { key: "customerEmpathy",   label: "Customer\nEmpathy",  max: 100 },
  { key: "solutionViability", label: "Solution\nViability", max: 90  },
  { key: "marketOpportunity", label: "Market\nOpportunity", max: 80  },
  { key: "founderReadiness",  label: "Founder\nReadiness",  max: 100 },
  { key: "businessModel",     label: "Business\nModel",     max: 80  },
  { key: "goToMarket",        label: "Go-To-\nMarket",      max: 90  },
  { key: "riskCompliance",    label: "Risk &\nCompliance",  max: 60  },
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function buildPolygonPoints(cx, cy, radii, angleStep) {
  return radii
    .map((r, i) => {
      const pt = polarToCartesian(cx, cy, r, i * angleStep);
      return `${pt.x},${pt.y}`;
    })
    .join(" ");
}

export default function ReportRadarChart({ res }) {
  const cx = 200;
  const cy = 200;
  const maxR = 150;
  const n = MODULES.length;
  const angleStep = 360 / n;

  const scores = MODULES.map((m) => res?.sectionScores?.[m.key] ?? 0);
  const radii = scores.map((s, i) => (s / MODULES[i].max) * maxR);

  const rings = [0.25, 0.5, 0.75, 1.0];
  const spokes = MODULES.map((_, i) => polarToCartesian(cx, cy, maxR, i * angleStep));
  const dataPoints = buildPolygonPoints(cx, cy, radii, angleStep);

  const totalScore = res?.totalScore ?? 0;
  const accentColor =
    totalScore >= 500 ? "#22C55E"
    : totalScore >= 400 ? "#84CC16"
    : totalScore >= 300 ? "#F59E0B"
    : "#EF4444";

  const gradId = "radarDataFill";
  const shadowId = "radarShadow";

  return (
    <div className="radar-section">
      <h2 className="radar-title">Performance Radar — All Dimensions</h2>
      <div className="radar-wrap">
        <div className="radar-svg-container">
          <svg viewBox="0 0 400 400" width="320" height="320">
            <defs>
              {/* Gradient fill for data polygon */}
              <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={accentColor} stopOpacity="0.35" />
                <stop offset="100%" stopColor={accentColor} stopOpacity="0.07" />
              </radialGradient>
              {/* Drop shadow for data polygon */}
              <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="6"
                  floodColor={accentColor}
                  floodOpacity="0.25"
                />
              </filter>
              {/* Subtle bg circle */}
              <radialGradient id="bgCircle" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="100%" stopColor="#f0f4ff" stopOpacity="1" />
              </radialGradient>
            </defs>

            {/* Background circle */}
            <circle cx={cx} cy={cy} r={maxR + 2} fill="url(#bgCircle)" />

            {/* Grid rings */}
            {rings.map((ratio, ri) => (
              <polygon
                key={ri}
                points={buildPolygonPoints(cx, cy, MODULES.map(() => maxR * ratio), angleStep)}
                fill="none"
                stroke={ri === 3 ? "#cbd5e1" : "#e2e8f0"}
                strokeWidth={ri === 3 ? 1.5 : 0.8}
                strokeDasharray={ri === 3 ? "none" : "4,4"}
              />
            ))}

            {/* Ring percentage labels */}
            {rings.map((ratio, ri) => {
              const pt = polarToCartesian(cx, cy, maxR * ratio, 0);
              return (
                <text
                  key={ri}
                  x={pt.x + 4}
                  y={pt.y - 3}
                  fontSize={7}
                  fill="#94a3b8"
                  fontFamily="Inter, sans-serif"
                >
                  {Math.round(ratio * 100)}%
                </text>
              );
            })}

            {/* Spokes */}
            {spokes.map((pt, i) => (
              <line
                key={i}
                x1={cx}
                y1={cy}
                x2={pt.x}
                y2={pt.y}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
            ))}

            {/* Data polygon */}
            <polygon
              points={dataPoints}
              fill={`url(#${gradId})`}
              stroke={accentColor}
              strokeWidth={2.5}
              strokeLinejoin="round"
              filter={`url(#${shadowId})`}
            />

            {/* Data dots — white outer ring + colored fill */}
            {radii.map((r, i) => {
              const pt = polarToCartesian(cx, cy, r, i * angleStep);
              return (
                <g key={i}>
                  <circle cx={pt.x} cy={pt.y} r={7} fill="#ffffff" />
                  <circle cx={pt.x} cy={pt.y} r={5} fill={accentColor} />
                  <circle cx={pt.x} cy={pt.y} r={2} fill="#ffffff" opacity={0.7} />
                </g>
              );
            })}

            {/* Labels */}
            {MODULES.map((m, i) => {
              const labelPt = polarToCartesian(cx, cy, maxR + 30, i * angleStep);
              const lines = m.label.split("\n");
              const score = scores[i];
              const pct = Math.round((score / m.max) * 100);
              return (
                <text
                  key={i}
                  x={labelPt.x}
                  y={labelPt.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={8.5}
                  fill="#475569"
                  fontFamily="Inter, sans-serif"
                  fontWeight="600"
                >
                  {lines.map((line, li) => (
                    <tspan key={li} x={labelPt.x} dy={li === 0 ? 0 : 10}>
                      {line}
                    </tspan>
                  ))}
                  <tspan
                    x={labelPt.x}
                    dy={11}
                    fill={accentColor}
                    fontWeight="700"
                    fontSize={8}
                  >
                    {score}/{m.max} ({pct}%)
                  </tspan>
                </text>
              );
            })}

            {/* Centre score background circle */}
            <circle cx={cx} cy={cy} r={32} fill="#ffffff" stroke="#f1f5f9" strokeWidth={1.5} />

            {/* Centre score */}
            <text
              x={cx}
              y={cy - 6}
              textAnchor="middle"
              fontSize={20}
              fontWeight="800"
              fill={accentColor}
              fontFamily="Inter, sans-serif"
            >
              {totalScore}
            </text>
            <text
              x={cx}
              y={cy + 10}
              textAnchor="middle"
              fontSize={8}
              fill="#94a3b8"
              fontFamily="Inter, sans-serif"
              fontWeight="500"
            >
              / 600
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="radar-legend">
          {MODULES.map((m, i) => {
            const pct = Math.round((scores[i] / m.max) * 100);
            const col =
              pct >= 90 ? "#22C55E"
              : pct >= 75 ? "#84CC16"
              : pct >= 60 ? "#F59E0B"
              : "#EF4444";
            return (
              <div key={i} className="radar-legend-row">
                <span className="radar-legend-dot" style={{ background: col }} />
                <span className="radar-legend-label">
                  {m.label.replace("\n", " ")}
                </span>
                <span className="radar-legend-score" style={{ color: col }}>
                  {scores[i]}/{m.max}
                </span>
                <span className="radar-legend-pct">{pct}%</span>
              </div>
            );
          })}

          {/* Total score badge */}
          <div className="radar-score-badge">
            <span className="radar-score-badge-label">Total Score</span>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span className="radar-score-badge-value">{totalScore}</span>
              <span className="radar-score-badge-max">/ 600</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
